/* MUI */
import { Box, Typography } from '@mui/material';

/* Shared */
import { Percentage } from 'application/Shared/Components/Text';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataVisualCard } from './DataVisualCard';

interface CalculatePercentageProps {
    countVar: string;
    data: unknown[];
}

const calculatePercentage = ({
    countVar,
    data
}: CalculatePercentageProps): number => {
    let sum = 0;

    if (!data?.length) {
        return 0;
    }

    Object.values(data).forEach((row) => {
        if (!row) {
            return;
        }
        const sliceVal = countVar
            .split('.')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .reduce((a: Record<string, any>, b: string) => a[b], row);
        if (sliceVal) {
            sum += 1;
        }
    });

    return sum;
};

interface DataAPIVisualsPercentageProps {
    card?: boolean;
    countVar: string;
    data: unknown[];
    filteredResults?: boolean;
    icon?: React.ReactNode;
    loading?: boolean;
    noDataMessage?: string;
    subtitle?: string;
    title: string;
    tooltip?: string;
}

export const DataAPIVisualsPercentage: React.FC<
    DataAPIVisualsPercentageProps
> = ({
    card,
    countVar,
    data,
    filteredResults,
    icon,
    loading,
    noDataMessage,
    subtitle,
    title,
    tooltip
}: DataAPIVisualsPercentageProps) => {
    const { t } = useTranslation();
    const [sum, setSum] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        const calculatedSum = calculatePercentage({ countVar, data });
        const currentTotal = data?.length;
        let currentPercentage = 0;
        if (calculatedSum > 0 && currentTotal !== 0) {
            currentPercentage = parseInt(
                ((calculatedSum / currentTotal) * 100).toString(),
                10
            );
        }
        setSum(calculatedSum);
        setPercentage(currentPercentage);
        setTotal(currentTotal);
    }, [countVar, data]);

    if (card) {
        return (
            <DataVisualCard
                filteredResults={filteredResults}
                icon={icon}
                loading={loading}
                subtitle={subtitle}
                title={title}
                tooltip={tooltip}
            >
                <Box
                    alignItems="center"
                    data-testid="dataAPIVisualsPercentageBoxWithCard"
                    data-trackid="dataAPIVisualsPercentageBox"
                    display="flex"
                    flexDirection="column"
                    height="100%"
                    justifyContent="center"
                    style={{
                        minHeight: '120px',
                        textAlign: 'center'
                    }}
                    width="100%"
                >
                    {data?.length ? (
                        <>
                            <Typography
                                color="text.cardKPI"
                                variant="h1"
                                style={{ fontSize: FontSizes[5] }}
                            >
                                <Percentage percent={percentage} />
                            </Typography>
                            <Typography
                                color="text.cardKPI"
                                variant="body2"
                                marginTop="4px"
                            >
                                {sum} / {total}
                            </Typography>
                        </>
                    ) : (
                        <Typography
                            color="text.cardKPINoData"
                            variant="body2"
                            sx={{ fontSize: FontSizes[2] }}
                        >
                            {noDataMessage || t('Common.noData')}
                        </Typography>
                    )}
                </Box>
            </DataVisualCard>
        );
    }
    return sum;
};
